import { useEffect, useRef, useState } from 'react';

import { useCreateFeedback, useUploadFeedbackAttachment } from '@/hooks/queries/feedback/use-feedback-mutations';
import {
	FEEDBACK_PRIORITY_LABELS,
	FEEDBACK_SEVERITY_LABELS,
	FEEDBACK_TYPE_COLORS,
	FEEDBACK_TYPE_LABELS,
	type FeedbackPriority,
	type FeedbackSeverity,
	type FeedbackType,
	type IFeedbackAttachment,
} from '@/interface/feedback';

import { captureScreenshot, createAudioRecorder, createScreenRecorder } from './capture-utils';
import { useFeedbackContext } from './FeedbackProvider';

type FormStep = 'type' | 'detail' | 'success';

const TYPE_DESCRIPTIONS: Record<FeedbackType, string> = {
	bug_report: 'Something is broken or not working correctly',
	feature_request: 'A new capability or enhancement',
	general_feedback: 'General comments or suggestions',
	error_report: 'An error was encountered',
};

const FeedbackForm = () => {
	const { closeWidget, getConsoleErrors } = useFeedbackContext();
	const createMutation = useCreateFeedback();
	const uploadMutation = useUploadFeedbackAttachment();

	const [step, setStep] = useState<FormStep>('type');
	const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [priority, setPriority] = useState<FeedbackPriority>('medium');
	const [severity, setSeverity] = useState<FeedbackSeverity | ''>('');
	const [attachments, setAttachments] = useState<IFeedbackAttachment[]>([]);
	const [showAdvanced, setShowAdvanced] = useState(false);
	const [showAttachments, setShowAttachments] = useState(true);
	const [recordingMode, setRecordingMode] = useState<'screen' | 'audio' | null>(null);
	const [recordingSeconds, setRecordingSeconds] = useState(0);
	const [createdId, setCreatedId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	const titleRef = useRef<HTMLInputElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const screenRecorderRef = useRef<ReturnType<typeof createScreenRecorder> | null>(null);
	const audioRecorderRef = useRef<ReturnType<typeof createAudioRecorder> | null>(null);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		if (step === 'detail') {
			setTimeout(() => titleRef.current?.focus(), 100);
		}
	}, [step]);

	useEffect(() => {
		if (step !== 'success') return;
		const timer = setTimeout(() => closeWidget(), 3000);
		return () => clearTimeout(timer);
	}, [step, closeWidget]);

	const handleTypeSelect = (type: FeedbackType) => {
		setSelectedType(type);
		setStep('detail');
	};

	const handleScreenshot = async (): Promise<void> => {
		const blob = await captureScreenshot();
		if (!blob) return;

		const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
		uploadMutation.mutate(
			{ file, type: 'screenshot' },
			{
				onSuccess: (att) => {
					setAttachments((prev) => [...prev, att]);
				},
			}
		);
	};

	const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files) return;

		Array.from(files).forEach((file) => {
			const type = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'file';
			uploadMutation.mutate(
				{ file, type },
				{
					onSuccess: (att) => {
						setAttachments((prev) => [...prev, att]);
					},
				}
			);
		});

		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	// Recording timer
	useEffect(() => {
		if (recordingMode) {
			setRecordingSeconds(0);
			timerRef.current = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
		} else {
			if (timerRef.current) clearInterval(timerRef.current);
			timerRef.current = null;
		}
		return () => {
			if (timerRef.current) clearInterval(timerRef.current);
		};
	}, [recordingMode]);

	// Cleanup recordings on unmount
	useEffect(
		() => () => {
			screenRecorderRef.current?.stop();
			audioRecorderRef.current?.stop();
		},
		[]
	);

	const formatTime = (seconds: number): string => {
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return `${m}:${s.toString().padStart(2, '0')}`;
	};

	const uploadRecordingBlob = (blob: Blob, type: 'recording' | 'audio') => {
		const file = new File([blob], `${type}-${Date.now()}.webm`, { type: blob.type });
		uploadMutation.mutate({ file, type }, { onSuccess: (att) => setAttachments((prev) => [...prev, att]) });
	};

	const handleStartScreenRecording = async () => {
		try {
			const recorder = createScreenRecorder();
			screenRecorderRef.current = recorder;
			recorder.onEnded((blob) => {
				setRecordingMode(null);
				screenRecorderRef.current = null;
				if (blob) uploadRecordingBlob(blob, 'recording');
			});
			await recorder.start(60_000);
			setRecordingMode('screen');
		} catch {
			screenRecorderRef.current = null;
		}
	};

	const handleStopScreenRecording = async () => {
		if (!screenRecorderRef.current) return;
		const blob = await screenRecorderRef.current.stop();
		setRecordingMode(null);
		screenRecorderRef.current = null;
		if (blob) uploadRecordingBlob(blob, 'recording');
	};

	const handleStartAudioRecording = async () => {
		try {
			const recorder = createAudioRecorder();
			audioRecorderRef.current = recorder;
			recorder.onEnded((blob) => {
				setRecordingMode(null);
				audioRecorderRef.current = null;
				if (blob) uploadRecordingBlob(blob, 'audio');
			});
			await recorder.start(120_000);
			setRecordingMode('audio');
		} catch {
			audioRecorderRef.current = null;
		}
	};

	const handleStopAudioRecording = async () => {
		if (!audioRecorderRef.current) return;
		const blob = await audioRecorderRef.current.stop();
		setRecordingMode(null);
		audioRecorderRef.current = null;
		if (blob) uploadRecordingBlob(blob, 'audio');
	};

	const removeAttachment = (id: number) => {
		setAttachments((prev) => prev.filter((a) => a.id !== id));
	};

	const handleSubmit = async () => {
		if (!selectedType || !title.trim() || !description.trim()) return;
		setError(null);

		createMutation.mutate(
			{
				type: selectedType,
				title: title.trim(),
				description: description.trim(),
				priority,
				severity: severity || undefined,
				pageUrl: window.location.href,
				browserInfo: navigator.userAgent,
				viewportSize: `${window.innerWidth}x${window.innerHeight}`,
				consoleErrors: getConsoleErrors(),
				attachmentIds: attachments.map((a) => a.id),
			},
			{
				onSuccess: (result) => {
					setCreatedId(result.id);
					setStep('success');
				},
				onError: () => {
					setError('Failed to submit feedback. Please try again.');
				},
			}
		);
	};

	const showSeverityField = selectedType === 'bug_report' || selectedType === 'error_report';

	// Step 1: Type selection
	if (step === 'type') {
		return (
			<div className='feedback-form'>
				<div className='feedback-form__header'>
					<h3>Submit Feedback</h3>
					<button onClick={closeWidget}>&times;</button>
				</div>
				<div className='feedback-form__body'>
					<div className='feedback-form__types'>
						{(Object.keys(FEEDBACK_TYPE_LABELS) as FeedbackType[]).map((type) => (
							<div
								key={type}
								className={`feedback-form__type-card feedback-form__type-card--${type}`}
								onClick={() => handleTypeSelect(type)}
								role='button'
								tabIndex={0}
								onKeyDown={(e) => e.key === 'Enter' && handleTypeSelect(type)}
							>
								<div
									className='feedback-form__type-card-title'
									style={{ color: FEEDBACK_TYPE_COLORS[type] }}
								>
									{FEEDBACK_TYPE_LABELS[type]}
								</div>
								<div className='feedback-form__type-card-desc'>{TYPE_DESCRIPTIONS[type]}</div>
							</div>
						))}
					</div>
				</div>
			</div>
		);
	}

	// Step 3: Success
	if (step === 'success') {
		return (
			<div className='feedback-form'>
				<div className='feedback-form__body'>
					<div className='feedback-form__success'>
						<div className='feedback-form__success-icon'>
							<svg
								width='24'
								height='24'
								viewBox='0 0 24 24'
								fill='none'
								stroke='currentColor'
								strokeWidth='2.5'
							>
								<polyline points='20 6 9 17 4 12' />
							</svg>
						</div>
						<div className='feedback-form__success-title'>Feedback submitted!</div>
						{createdId && <div className='feedback-form__success-id'>Feedback #{createdId}</div>}
					</div>
				</div>
			</div>
		);
	}

	// Step 2: Detail form
	return (
		<div className='feedback-form'>
			<div className='feedback-form__header'>
				<h3>{selectedType ? FEEDBACK_TYPE_LABELS[selectedType] : 'Feedback'}</h3>
				<button onClick={closeWidget}>&times;</button>
			</div>
			<div className='feedback-form__body'>
				<div className='feedback-form__detail'>
					<button
						className='feedback-form__back'
						onClick={() => setStep('type')}
					>
						&larr; Back
					</button>

					<div className='feedback-form__field'>
						<label>Title *</label>
						<input
							ref={titleRef}
							type='text'
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							placeholder='Brief summary'
							maxLength={500}
						/>
					</div>

					<div className='feedback-form__field'>
						<label>Description *</label>
						<textarea
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder='Describe in detail...'
							rows={3}
						/>
					</div>

					{/* Advanced options */}
					{showSeverityField && (
						<>
							<button
								className='feedback-form__back'
								style={{ fontSize: 'var(--font-size-xs)' }}
								onClick={() => setShowAdvanced(!showAdvanced)}
							>
								{showAdvanced ? '- Hide' : '+'} Advanced options
							</button>
							{showAdvanced && (
								<>
									<div className='feedback-form__field'>
										<label>Priority</label>
										<select
											value={priority}
											onChange={(e) => setPriority(e.target.value as FeedbackPriority)}
										>
											{Object.entries(FEEDBACK_PRIORITY_LABELS).map(([key, label]) => (
												<option
													key={key}
													value={key}
												>
													{label}
												</option>
											))}
										</select>
									</div>
									<div className='feedback-form__field'>
										<label>Severity</label>
										<select
											value={severity}
											onChange={(e) => setSeverity(e.target.value as FeedbackSeverity | '')}
										>
											<option value=''>Not set</option>
											{Object.entries(FEEDBACK_SEVERITY_LABELS).map(([key, label]) => (
												<option
													key={key}
													value={key}
												>
													{label}
												</option>
											))}
										</select>
									</div>
								</>
							)}
						</>
					)}

					{/* Attachments */}
					<div className='feedback-form__attachments'>
						<button
							className='feedback-form__attachments-toggle'
							onClick={() => setShowAttachments(!showAttachments)}
							type='button'
						>
							<svg
								width='12'
								height='12'
								viewBox='0 0 24 24'
								fill='none'
								stroke='currentColor'
								strokeWidth='2.5'
								style={{
									transform: showAttachments ? 'rotate(0)' : 'rotate(180deg)',
									transition: 'transform 0.15s ease',
								}}
							>
								<polyline points='18 15 12 9 6 15' />
							</svg>
							<span>Attachments</span>
							{attachments.length > 0 && <span className='feedback-form__attachments-badge'>{attachments.length}</span>}
						</button>

						{showAttachments && (
							<div className='feedback-form__attachments-content'>
								{/* Recording state banner */}
								{recordingMode && (
									<div className={`feedback-form__recording feedback-form__recording--${recordingMode}`}>
										<div className='feedback-form__recording-info'>
											<span className='feedback-form__recording-dot' />
											<span className='feedback-form__recording-label'>
												{recordingMode === 'screen' ? 'Recording screen' : 'Recording audio'}
											</span>
											<span className='feedback-form__recording-timer'>{formatTime(recordingSeconds)}</span>
										</div>
										<button
											className='feedback-form__recording-stop'
											onClick={recordingMode === 'screen' ? handleStopScreenRecording : handleStopAudioRecording}
											type='button'
											aria-label='Stop recording'
										>
											<svg
												width='10'
												height='10'
												viewBox='0 0 16 16'
												fill='currentColor'
											>
												<rect
													x='2'
													y='2'
													width='12'
													height='12'
													rx='1.5'
												/>
											</svg>
											Stop
										</button>
									</div>
								)}

								{/* Capture action buttons */}
								{!recordingMode && (
									<div className='feedback-form__capture-grid'>
										<button
											className='feedback-form__capture-btn'
											onClick={handleScreenshot}
											disabled={uploadMutation.isPending}
											type='button'
										>
											<svg
												width='14'
												height='14'
												viewBox='0 0 24 24'
												fill='none'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'
											>
												<path d='M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z' />
												<circle
													cx='12'
													cy='13'
													r='4'
												/>
											</svg>
											Screenshot
										</button>
										<button
											className='feedback-form__capture-btn'
											onClick={handleStartScreenRecording}
											type='button'
										>
											<svg
												width='14'
												height='14'
												viewBox='0 0 24 24'
												fill='none'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'
											>
												<rect
													x='2'
													y='3'
													width='20'
													height='14'
													rx='2'
												/>
												<line
													x1='8'
													y1='21'
													x2='16'
													y2='21'
												/>
												<line
													x1='12'
													y1='17'
													x2='12'
													y2='21'
												/>
												<circle
													cx='12'
													cy='10'
													r='2'
													fill='currentColor'
													stroke='none'
												/>
											</svg>
											Record
										</button>
										<button
											className='feedback-form__capture-btn'
											onClick={handleStartAudioRecording}
											type='button'
										>
											<svg
												width='14'
												height='14'
												viewBox='0 0 24 24'
												fill='none'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'
											>
												<path d='M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z' />
												<path d='M19 10v2a7 7 0 0 1-14 0v-2' />
												<line
													x1='12'
													y1='19'
													x2='12'
													y2='23'
												/>
												<line
													x1='8'
													y1='23'
													x2='16'
													y2='23'
												/>
											</svg>
											Audio
										</button>
										<button
											className='feedback-form__capture-btn'
											onClick={() => fileInputRef.current?.click()}
											type='button'
										>
											<svg
												width='14'
												height='14'
												viewBox='0 0 24 24'
												fill='none'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'
											>
												<path d='M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48' />
											</svg>
											Upload
										</button>
										<input
											ref={fileInputRef}
											type='file'
											hidden
											accept='image/*,video/*,.pdf,.doc,.docx,.txt'
											multiple
											onChange={handleFileUpload}
										/>
									</div>
								)}

								{/* Attachment list */}
								{attachments.length > 0 && (
									<div className='feedback-form__attachments-list'>
										{attachments.map((att) => (
											<div
												key={att.id}
												className='feedback-form__attachments-item'
											>
												<span className='feedback-form__attachments-item-name'>{att.originalFilename}</span>
												<button
													onClick={() => removeAttachment(att.id)}
													type='button'
													aria-label={`Remove ${att.originalFilename}`}
												>
													&times;
												</button>
											</div>
										))}
									</div>
								)}
							</div>
						)}
					</div>

					{error && <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)', margin: 0 }}>{error}</p>}

					<div className='feedback-form__actions'>
						<button
							className='btn btn-sm btn-outline-secondary'
							onClick={closeWidget}
						>
							Cancel
						</button>
						<button
							className='btn btn-sm btn-primary'
							onClick={handleSubmit}
							disabled={!title.trim() || !description.trim() || createMutation.isPending}
						>
							{createMutation.isPending ? 'Submitting...' : 'Submit'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default FeedbackForm;
